import { Injectable, signal } from '@angular/core';

/**
 * Interfaccia per le opzioni del toast
 */
export interface IToastOptions {
  /** Messaggio da mostrare */
  message: string;
  /** Tipo di toast: 'success', 'error', 'warning', 'info' */
  type?: 'success' | 'error' | 'warning' | 'info';
  /** Durata in millisecondi (default: 5000). Se 0, nessun autohide (serve pulsante chiudi). */
  duration?: number;
  /** Posizione (riservata per estensioni future; attualmente il container è fisso in alto a destra) */
  position?: 'top-start' | 'top-center' | 'top-end' | 'bottom-start' | 'bottom-center' | 'bottom-end';
  /** Mostra pulsante di chiusura */
  showCloseButton?: boolean;
}

/** Voce in coda mostrata da {@link ToastHostComponent} tramite `ngb-toast` */
export interface IToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration: number;
  showCloseButton: boolean;
}

/**
 * Notifiche toast centralizzate: coda reattiva consumata dal componente root `app-toast-host`
 * (ng-bootstrap `NgbToast`, senza Bootstrap JS).
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  /** Coda toast per il template (`@for` in `app-toast-host`) */
  readonly queue = signal<IToastItem[]>([]);

  private readonly defaultDuration = 5000;

  private enqueue(options: IToastOptions): void {
    const {
      message,
      type = 'info',
      duration = this.defaultDuration,
      showCloseButton = true
    } = options;

    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const item: IToastItem = {
      id,
      message,
      type,
      duration,
      showCloseButton
    };
    this.queue.update((q) => [...q, item]);
  }

  /** Rimuove un toast dalla coda (chiamato da `(hidden)` su `ngb-toast` o pulsante chiudi). */
  remove(id: string): void {
    this.queue.update((q) => q.filter((t) => t.id !== id));
  }

  presentaToastSuccess(message: string, duration?: number): void {
    this.enqueue({
      message,
      type: 'success',
      duration: duration ?? this.defaultDuration
    });
  }

  presentaToastErrore(message: string, duration?: number): void {
    this.enqueue({
      message,
      type: 'error',
      duration: duration ?? this.defaultDuration
    });
  }

  presentaToastWarning(message: string, duration?: number): void {
    this.enqueue({
      message,
      type: 'warning',
      duration: duration ?? this.defaultDuration
    });
  }

  presentaToastInfo(message: string, duration?: number): void {
    this.enqueue({
      message,
      type: 'info',
      duration: duration ?? this.defaultDuration
    });
  }

  presentaToast(options: IToastOptions): void {
    this.enqueue(options);
  }
}
